import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  GlobeIcon,
  LayoutTemplateIcon,
  Loader2Icon,
  LockIcon,
  PencilIcon,
  PlayCircleIcon,
  SearchIcon,
  Share2Icon,
  SparklesIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Elements } from "@/components/elements/Elements";
import { TemplateDescriptionEditor } from "@/components/elements/ProfileSelector/TemplateDescriptionEditor";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogMaximize,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ModalHeader,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from "@/components/ui/responsive-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useApproveTemplate } from "@/hooks/queries/useApproveTemplate";
import { useLucideIcons } from "@/hooks/useLucideIcons";
import { useCreateProfileFromTemplate } from "@/hooks/queries/useCreateProfileFromTemplate";
import { useGetTemplateCategories } from "@/hooks/queries/useGetTemplateCategories";
import { useGetTemplate } from "@/hooks/queries/useGetTemplate";
import { useGetTemplates } from "@/hooks/queries/useGetTemplates";
import { useGetUserTeams } from "@/hooks/queries/useGetUserTeams";
import { useUpdateTemplate } from "@/hooks/queries/useUpdateTemplate";
import { useFileSignedUrlMap } from "@/hooks/useFileSignedUrlMap";
import { useElements } from "@/hooks/useElementsProvider";
import { useUser } from "@/hooks/useUserProvider";
import {
  generateTemplateThumbnailFromSnapshot,
  getTemplateWidgetBreakdownFromSnapshot,
} from "@/lib/template-thumbnail";
import { stripTemplateDescriptionMarkdown } from "@/lib/template-description";
import { cn } from "@/lib/utils";
import { Template, TemplateVisibility } from "@/lib/types";
import { openProfileTeamSharingManager } from "@/components/elements/ProfileSelector/team-sharing-events";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";
import {
  getYouTubeEmbedUrl,
  getYouTubeThumbnailUrl,
  isYouTubeUrl,
} from "@/lib/youtube";

const visibilityOptions: {
  value: "all" | TemplateVisibility;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: "all", label: "Any", icon: SparklesIcon },
  { value: "public", label: "Public", icon: GlobeIcon },
  { value: "team", label: "Team", icon: UsersIcon },
  { value: "private", label: "Private", icon: LockIcon },
];

const editableVisibilityModes: {
  value: TemplateVisibility;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  {
    value: "private",
    title: "Private",
    description: "Only you can discover and use this template.",
    icon: LockIcon,
  },
  {
    value: "public",
    title: "Public",
    description: "Anyone with access can find and use this template.",
    icon: GlobeIcon,
  },
  {
    value: "team",
    title: "Team",
    description: "Only members of a selected team can use it.",
    icon: UsersIcon,
  },
];

const visibilityLabels: Record<TemplateVisibility, string> = {
  private: "Private",
  public: "Public",
  team: "Team",
};

const visibilityIcons: Record<
  TemplateVisibility,
  React.ComponentType<{ className?: string }>
> = {
  private: LockIcon,
  public: GlobeIcon,
  team: UsersIcon,
};

const publicationStatusLabels = {
  pending: "Pending approval",
  approved: "Verified",
  rejected: "Rejected",
  needs_change: "Needs changes",
} as const;

const getPublicationStatusLabel = (
  template: Pick<Template, "publicationStatus" | "isByUnideck">,
) => {
  if (template.publicationStatus === "approved") {
    return template.isByUnideck ? "UniDeck Official" : "Verified";
  }

  return (
    publicationStatusLabels[template.publicationStatus] ||
    template.publicationStatus
  );
};

const formatRelativeDate = (value: Date | string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "recently";
  }

  return formatDistanceToNow(date, { addSuffix: true });
};

const normalizeWidgetLookupKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const humanizeWidgetName = (value: string) =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .trim();

const getTemplateSearchText = (template: Template) => {
  return [
    template.name,
    stripTemplateDescriptionMarkdown(template.description || ""),
    template.visibility,
    template.category?.name || "",
    template.subcategory?.name || "",
    template.team?.name || "",
    template.owner?.displayName || "",
    ...(template.tags || []),
  ]
    .join(" ")
    .toLowerCase();
};

const toPascalCase = (value: string) =>
  value
    .split(/[^a-zA-Z0-9]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

const getTemplateIconComponent = (
  lucideIcons: Record<string, LucideIcon> | null,
  iconName: string | null | undefined,
) => {
  if (!iconName || !lucideIcons) {
    return LayoutTemplateIcon;
  }

  return lucideIcons[toPascalCase(iconName)] || LayoutTemplateIcon;
};

const iconColorClasses = {
  orange: "text-orange-300 border-orange-500/35 bg-orange-500/15",
  teal: "text-teal-300 border-teal-500/35 bg-teal-500/15",
  purple: "text-violet-300 border-violet-500/35 bg-violet-500/15",
  blue: "text-sky-300 border-sky-500/35 bg-sky-500/15",
  red: "text-rose-300 border-rose-500/35 bg-rose-500/15",
  yellow: "text-amber-300 border-amber-500/35 bg-amber-500/15",
} as const;

const getTemplateIconColorClassName = (color: string | null | undefined) => {
  if (!color) {
    return "text-primary border-primary/35 bg-primary/15";
  }

  return (
    iconColorClasses[color.toLowerCase() as keyof typeof iconColorClasses] ||
    "text-primary border-primary/35 bg-primary/15"
  );
};

export const ExploreTemplates = ({
  open: openProp,
  onOpenChange,
  initialCategoryId,
  hideTrigger = false,
  onTemplateUsed,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialCategoryId?: string | null;
  hideTrigger?: boolean;
  onTemplateUsed?: (template: { id: string; name: string }) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { accessToken, syncUser } = useUser();
  const { syncProfiles, setActiveProfileId } = useElements();
  const lucideIcons = useLucideIcons();

  const isControlledOpen = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlledOpen ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlledOpen) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };
  const [detailsTemplateId, setDetailsTemplateId] = useState<string | null>(
    null,
  );
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [query, setQuery] = useState("");
  const [visibility, setVisibility] = useState<"all" | TemplateVisibility>(
    "all",
  );
  const [categoryFilterId, setCategoryFilterId] = useState<string>("all");
  const [subcategoryFilterId, setSubcategoryFilterId] = useState<string>("all");
  const [usingTemplateId, setUsingTemplateId] = useState<string | null>(null);
  const [approvingTemplateId, setApprovingTemplateId] = useState<string | null>(
    null,
  );
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTagsInput, setEditTagsInput] = useState("");
  const [editDemoVideoUrl, setEditDemoVideoUrl] = useState("");
  const [editVisibility, setEditVisibility] =
    useState<TemplateVisibility>("private");
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [editSubcategoryId, setEditSubcategoryId] = useState<string | null>(
    null,
  );
  const [editTeamId, setEditTeamId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [detailsMediaSlideIndex, setDetailsMediaSlideIndex] = useState(0);

  const {
    data: templates,
    isLoading,
    isError,
    error,
  } = useGetTemplates({
    enabled: open,
  });
  const {
    data: templateCategories,
    isLoading: isLoadingTemplateCategories,
    isError: isTemplateCategoriesError,
    error: templateCategoriesError,
  } = useGetTemplateCategories({
    enabled: open,
  });

  const { mutateAsync: createProfileFromTemplate } =
    useCreateProfileFromTemplate();
  const { mutateAsync: approveTemplate } = useApproveTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplate();
  const {
    data: editableTeams,
    isLoading: isLoadingEditableTeams,
    isError: isEditableTeamsError,
    error: editableTeamsError,
  } = useGetUserTeams({
    enabled: open && !!editingTemplate,
  });
  const {
    data: detailsTemplate,
    isLoading: isLoadingDetailsTemplate,
    isError: isDetailsTemplateError,
    error: detailsTemplateError,
    refetch: refetchDetailsTemplate,
  } = useGetTemplate({
    templateId: detailsTemplateId || "",
    enabled: open && !!detailsTemplateId,
  });

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return (templates || []).filter(template => {
      if (visibility !== "all" && template.visibility !== visibility) {
        return false;
      }

      if (
        categoryFilterId !== "all" &&
        (template.category?.id || template.categoryId) !== categoryFilterId
      ) {
        return false;
      }

      if (
        subcategoryFilterId !== "all" &&
        (template.subcategory?.id || template.subcategoryId) !==
          subcategoryFilterId
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return getTemplateSearchText(template).includes(normalizedQuery);
    });
  }, [templates, query, visibility, categoryFilterId, subcategoryFilterId]);

  const hasActiveExplorerFilters =
    visibility !== "all" ||
    categoryFilterId !== "all" ||
    subcategoryFilterId !== "all" ||
    query.trim().length > 0;

  const explorerCardThumbnailFileIds = useMemo(
    () =>
      filteredTemplates.flatMap(template =>
        (template.thumbnailFileIds || []).filter(
          (fileId): fileId is string => !!fileId,
        ),
      ),
    [filteredTemplates],
  );

  const detailsThumbnailFileIds = useMemo(
    () => detailsTemplate?.thumbnailFileIds || [],
    [detailsTemplate?.thumbnailFileIds],
  );

  const requestedTemplateThumbnailFileIds = useMemo(
    () => [
      ...new Set([...explorerCardThumbnailFileIds, ...detailsThumbnailFileIds]),
    ],
    [detailsThumbnailFileIds, explorerCardThumbnailFileIds],
  );

  const { signedUrlByFileId: templateThumbnailSignedUrlByFileId } =
    useFileSignedUrlMap({
      fileIds: requestedTemplateThumbnailFileIds,
      enabled: open,
    });

  const detailsWidgets = useMemo(
    () =>
      getTemplateWidgetBreakdownFromSnapshot(detailsTemplate?.elements || []),
    [detailsTemplate?.elements],
  );
  const widgetDescriptionLookup = useMemo(() => {
    const lookup = new Map<string, string>();

    Object.entries(Elements).forEach(([elementKey, element]) => {
      const details = element?.details as
        | { name?: string; description?: string }
        | undefined;

      if (!details?.description) {
        return;
      }

      lookup.set(normalizeWidgetLookupKey(elementKey), details.description);
      if (details.name) {
        lookup.set(normalizeWidgetLookupKey(details.name), details.description);
      }
    });

    return lookup;
  }, []);
  const detailsWidgetsWithDescription = useMemo(() => {
    return detailsWidgets.map(widget => ({
      ...widget,
      label: humanizeWidgetName(widget.name),
      description:
        widgetDescriptionLookup.get(normalizeWidgetLookupKey(widget.name)) ||
        "No description available for this widget yet.",
    }));
  }, [detailsWidgets, widgetDescriptionLookup]);
  const detailsTotalWidgets = useMemo(
    () => detailsWidgets.reduce((total, widget) => total + widget.count, 0),
    [detailsWidgets],
  );
  const detailsGeneratedThumbnail = useMemo(() => {
    if (!detailsTemplate) {
      return "";
    }

    return generateTemplateThumbnailFromSnapshot({
      elements: detailsTemplate.elements || [],
      title: detailsTemplate.name,
      subtitle: `${detailsTotalWidgets} widget${detailsTotalWidgets === 1 ? "" : "s"}`,
    });
  }, [detailsTemplate, detailsTotalWidgets]);
  const detailsDemoVideoEmbedUrl = getYouTubeEmbedUrl(
    detailsTemplate?.demoVideoUrl,
  );
  const detailsThumbnailSources = useMemo(
    () =>
      (detailsTemplate?.thumbnailFileIds || [])
        .map(fileId => templateThumbnailSignedUrlByFileId[fileId])
        .filter((url): url is string => !!url),
    [detailsTemplate?.thumbnailFileIds, templateThumbnailSignedUrlByFileId],
  );
  const detailsMediaSlides = useMemo(() => {
    const slides: { kind: "video" | "image"; src: string }[] = [];

    if (detailsDemoVideoEmbedUrl) {
      slides.push({
        kind: "video",
        src: detailsDemoVideoEmbedUrl,
      });
    }

    if (detailsThumbnailSources.length) {
      detailsThumbnailSources.forEach(src => {
        slides.push({
          kind: "image",
          src,
        });
      });
    } else if (detailsGeneratedThumbnail) {
      slides.push({
        kind: "image",
        src: detailsGeneratedThumbnail,
      });
    }

    return slides;
  }, [
    detailsDemoVideoEmbedUrl,
    detailsGeneratedThumbnail,
    detailsThumbnailSources,
  ]);
  const detailsActiveSlide =
    detailsMediaSlides[detailsMediaSlideIndex] || detailsMediaSlides[0] || null;
  const detailsDescriptionMarkdown = detailsTemplate?.description?.trim()
    ? detailsTemplate.description
    : "No description provided.";
  const canManageTemplate = (template: Template) => template.canManage;
  const selectedEditCategory = useMemo(() => {
    if (!editCategoryId) {
      return null;
    }

    return (
      templateCategories?.find(category => category.id === editCategoryId) ||
      null
    );
  }, [editCategoryId, templateCategories]);

  // When opened with a preset category (e.g. from onboarding), narrow the
  // gallery to it. Runs on open so manual category changes while open aren't
  // clobbered.
  useEffect(() => {
    if (open && initialCategoryId) {
      setCategoryFilterId(initialCategoryId);
      setSubcategoryFilterId("all");
    }
  }, [open, initialCategoryId]);

  useEffect(() => {
    if (categoryFilterId === "all") {
      if (subcategoryFilterId !== "all") {
        setSubcategoryFilterId("all");
      }
      return;
    }

    if (!templateCategories?.length) {
      return;
    }

    const selectedCategory = templateCategories.find(
      category => category.id === categoryFilterId,
    );

    if (!selectedCategory) {
      setCategoryFilterId("all");
      setSubcategoryFilterId("all");
      return;
    }

    if (subcategoryFilterId === "all") {
      return;
    }

    if (
      selectedCategory.subcategories.some(
        subcategory => subcategory.id === subcategoryFilterId,
      )
    ) {
      return;
    }

    setSubcategoryFilterId("all");
  }, [categoryFilterId, subcategoryFilterId, templateCategories]);

  useEffect(() => {
    if (!editingTemplate || !templateCategories?.length) {
      return;
    }

    if (editCategoryId && editSubcategoryId) {
      return;
    }

    const preferredCategory =
      (editingTemplate.category?.id
        ? templateCategories.find(
            category => category.id === editingTemplate.category?.id,
          )
        : undefined) || templateCategories[0];

    setEditCategoryId(preferredCategory.id);

    const preferredSubcategory =
      (editingTemplate.subcategory?.id
        ? preferredCategory.subcategories.find(
            subcategory => subcategory.id === editingTemplate.subcategory?.id,
          )
        : undefined) || preferredCategory.subcategories[0];

    setEditSubcategoryId(preferredSubcategory?.id || null);
  }, [editCategoryId, editSubcategoryId, editingTemplate, templateCategories]);

  useEffect(() => {
    if (!editCategoryId || !templateCategories?.length) {
      return;
    }

    const nextCategory = templateCategories.find(
      category => category.id === editCategoryId,
    );

    if (!nextCategory) {
      setEditSubcategoryId(null);
      return;
    }

    if (
      editSubcategoryId &&
      nextCategory.subcategories.some(
        subcategory => subcategory.id === editSubcategoryId,
      )
    ) {
      return;
    }

    setEditSubcategoryId(nextCategory.subcategories[0]?.id || null);
  }, [editCategoryId, editSubcategoryId, templateCategories]);

  useEffect(() => {
    if (!editingTemplate || editVisibility !== "team") {
      return;
    }

    if (editTeamId || !editableTeams?.length) {
      return;
    }

    const preferredTeam =
      (editingTemplate.team?.id || editingTemplate.teamId
        ? editableTeams.find(
            team =>
              team.id === (editingTemplate.team?.id || editingTemplate.teamId),
          )
        : undefined) || editableTeams[0];

    setEditTeamId(preferredTeam.id);
  }, [editTeamId, editVisibility, editableTeams, editingTemplate]);

  useEffect(() => {
    setDetailsMediaSlideIndex(0);
  }, [detailsTemplateId]);

  useEffect(() => {
    if (!detailsMediaSlides.length) {
      if (detailsMediaSlideIndex !== 0) {
        setDetailsMediaSlideIndex(0);
      }
      return;
    }

    if (detailsMediaSlideIndex < detailsMediaSlides.length) {
      return;
    }

    setDetailsMediaSlideIndex(detailsMediaSlides.length - 1);
  }, [detailsMediaSlideIndex, detailsMediaSlides.length]);

  const handleUseTemplate = async (
    templateId: string,
    templateName: string,
  ) => {
    setUsingTemplateId(templateId);
    try {
      const profile = await createProfileFromTemplate({
        templateId,
      });

      await syncProfiles();
      await setActiveProfileId(profile.id);
      await syncUser();

      captureEvent("dashboard_profile_created", {
        profileId: profile.id,
        profileName: profile.name,
        source: "template",
      });
      captureEvent("dashboard_template_used", {
        templateId,
        templateName,
        profileId: profile.id,
      });

      toast.success(`Created "${profile.name}" from "${templateName}".`);
      setDetailsTemplateId(null);
      setOpen(false);
      onTemplateUsed?.({ id: templateId, name: templateName });
    } catch (err) {
      captureEvent("api_mutation_failed", {
        mutationKey: '["createProfileFromTemplate"]',
        reason: sanitizeErrorReason(err),
      });
      toast.error(
        err instanceof Error ? err.message : "Failed to use this template.",
      );
    } finally {
      setUsingTemplateId(null);
    }
  };

  const handleApproveTemplate = async (template: Template) => {
    if (!template.canApprove) {
      return;
    }

    setApprovingTemplateId(template.id);
    try {
      await approveTemplate({ templateId: template.id });
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (detailsTemplateId === template.id) {
        await refetchDetailsTemplate();
      }
      toast.success(`"${template.name}" is now approved for public listing.`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to approve template.",
      );
    } finally {
      setApprovingTemplateId(null);
    }
  };

  const handleOpenTemplateDetails = (templateId: string) => {
    setDetailsTemplateId(templateId);
  };

  const handleOpenTeamSharingModal = () => {
    setDetailsTemplateId(null);
    resetEditState();
    setOpen(false);
    setTimeout(() => {
      openProfileTeamSharingManager();
    }, 0);
  };

  const resetExplorerFilters = () => {
    setQuery("");
    setVisibility("all");
    setCategoryFilterId("all");
    setSubcategoryFilterId("all");
  };

  const resetEditState = () => {
    setEditingTemplate(null);
    setEditName("");
    setEditDescription("");
    setEditTagsInput("");
    setEditDemoVideoUrl("");
    setEditVisibility("private");
    setEditCategoryId(null);
    setEditSubcategoryId(null);
    setEditTeamId(null);
    setEditError(null);
  };

  const handleOpenTemplateEditor = (template: Template) => {
    if (!canManageTemplate(template)) {
      return;
    }

    setEditingTemplate(template);
    setEditName(template.name || "");
    setEditDescription(template.description || "");
    setEditTagsInput((template.tags || []).join(", "));
    setEditDemoVideoUrl(template.demoVideoUrl || "");
    setEditVisibility(template.visibility);
    setEditCategoryId(template.category?.id || template.categoryId || null);
    setEditSubcategoryId(
      template.subcategory?.id || template.subcategoryId || null,
    );
    setEditTeamId(template.team?.id || template.teamId || null);
    setEditError(null);
  };

  const handleSaveTemplateDetails = async () => {
    if (!editingTemplate) {
      return;
    }

    const trimmedName = editName.trim();
    if (!trimmedName.length) {
      setEditError("Template name is required.");
      return;
    }

    const trimmedDescription = editDescription.trim();
    if (editDescription.length > 0 && !trimmedDescription.length) {
      setEditError("Description cannot be whitespace only.");
      return;
    }

    const normalizedTags = [
      ...new Set(
        editTagsInput
          .split(",")
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0),
      ),
    ];

    if (normalizedTags.length > 25) {
      setEditError("You can add up to 25 tags.");
      return;
    }

    const normalizedDemoVideoUrl = editDemoVideoUrl.trim();
    if (normalizedDemoVideoUrl && !isYouTubeUrl(normalizedDemoVideoUrl)) {
      setEditError("Demo video URL must be a valid YouTube link.");
      return;
    }

    if (!editCategoryId || !editSubcategoryId) {
      setEditError("Please select both a category and subcategory.");
      return;
    }

    if (editVisibility === "team" && !editTeamId) {
      setEditError("Please select a team for team visibility.");
      return;
    }

    setEditError(null);

    try {
      await updateTemplate({
        templateId: editingTemplate.id,
        name: trimmedName,
        description: trimmedDescription || undefined,
        categoryId: editCategoryId,
        subcategoryId: editSubcategoryId,
        visibility: editVisibility,
        teamId:
          editVisibility === "team" ? (editTeamId ?? undefined) : undefined,
        tags: normalizedTags,
        demoVideoUrl: normalizedDemoVideoUrl || undefined,
      });

      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (detailsTemplateId === editingTemplate.id) {
        await refetchDetailsTemplate();
      }
      toast.success(`Updated "${trimmedName}" successfully.`);
      resetEditState();
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : "Failed to update template.",
      );
    }
  };

  if (!accessToken) {
    return null;
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setDetailsTemplateId(null);
          resetEditState();
          resetExplorerFilters();
        }
      }}
    >
      {hideTrigger ? null : (
        <ResponsiveDialogTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            aria-label="Explore Templates"
            className="bg-secondary/50 hover:bg-secondary rounded-xl px-2 shadow-sm backdrop-blur transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] lg:px-4"
          >
            <LayoutTemplateIcon className="size-4" />
            <span className="hidden font-medium xl:inline">
              Explore Templates
            </span>
            <span className="hidden font-medium lg:inline xl:hidden">
              Templates
            </span>
          </Button>
        </ResponsiveDialogTrigger>
      )}
      <ResponsiveDialogContent
        disableClose
        className="flex h-[min(90vh,920px)] max-w-[1120px] flex-col overflow-hidden p-4 sm:p-5"
        dialogId="explore-templates-v2"
        defaultMaximized
        drawerClassName="h-[92dvh] bg-card text-foreground"
      >
        <ModalHeader
          icon={LayoutTemplateIcon}
          title={
            <span className="flex items-center gap-2">
              Explore Templates
              <Badge
                variant="secondary"
                className="border-primary/30 bg-primary/10 text-primary rounded-full border px-2 py-0 text-[10px] font-semibold tracking-wide uppercase"
              >
                Beta
              </Badge>
            </span>
          }
          description="Discover reusable layouts made by you and our community to create a new dashboard in one click."
          actions={<DialogMaximize className="hidden lg:inline-flex" />}
        />
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <button
            type="button"
            onClick={handleOpenTeamSharingModal}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 self-start text-[11px] transition-colors"
          >
            <Share2Icon className="size-3.5 shrink-0" />
            <span>
              Want live team collaboration? Use{" "}
              <span className="text-foreground font-medium">Team Sharing</span>{" "}
              instead.
            </span>
          </button>
          <div className="flex flex-col gap-2.5">
            <div className="relative">
              <Input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search templates..."
                className="border-border/60 bg-background/60 h-10 rounded-lg pr-9 pl-10"
              />
              <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              {query.trim().length ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1 size-7 -translate-y-1/2 rounded-md"
                  onClick={() => setQuery("")}
                >
                  <XIcon className="size-3.5" />
                  <span className="sr-only">Clear search</span>
                </Button>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1 overflow-x-auto pb-1 lg:pb-0">
                <div className="flex w-max items-center gap-1.5 pr-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className={cn(
                      "h-8 shrink-0 rounded-lg border px-2.5 text-xs",
                      categoryFilterId === "all"
                        ? "border-primary/45 bg-primary/10 text-primary"
                        : "border-border/60 bg-background/45 hover:bg-accent/60",
                    )}
                    onClick={() => {
                      setCategoryFilterId("all");
                      setSubcategoryFilterId("all");
                    }}
                  >
                    <SparklesIcon className="size-3.5" />
                    All
                  </Button>

                  {(templateCategories || []).map(category => {
                    const CategoryIcon = getTemplateIconComponent(
                      lucideIcons,
                      category.icon?.name,
                    );
                    const categoryIconColorClassName =
                      getTemplateIconColorClassName(category.icon?.color);
                    const isCategoryActive = categoryFilterId === category.id;

                    return (
                      <Button
                        key={category.id}
                        size="sm"
                        variant="outline"
                        className={cn(
                          "h-8 rounded-lg border px-2.5 text-xs",
                          isCategoryActive
                            ? "border-primary/45 bg-primary/10 text-primary"
                            : "border-border/60 bg-background/45 hover:bg-accent/60",
                        )}
                        onClick={() => {
                          setCategoryFilterId(category.id);
                          setSubcategoryFilterId("all");
                        }}
                      >
                        <span
                          className={cn(
                            "flex size-4.5 items-center justify-center rounded-md border",
                            categoryIconColorClassName,
                          )}
                        >
                          <CategoryIcon className="size-3" />
                        </span>
                        <span className="max-w-[120px] truncate">
                          {category.name}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                <Select
                  value={visibility}
                  onValueChange={value =>
                    setVisibility(value as "all" | TemplateVisibility)
                  }
                >
                  <SelectTrigger className="border-border/60 bg-background/45 h-8 w-auto min-w-[7rem] rounded-lg text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {visibilityOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-xs"
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="size-3.5" />
                            {option.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {hasActiveExplorerFilters ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground h-8 rounded-lg px-2.5 text-xs"
                    onClick={resetExplorerFilters}
                  >
                    Reset
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="grid min-h-0 flex-1 auto-rows-max [grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr))] content-start gap-3 overflow-y-auto p-1">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-[440px] rounded-2xl" />
              ))}
            </div>
          ) : isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load templates</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : "An error occurred."}
              </AlertDescription>
            </Alert>
          ) : filteredTemplates.length === 0 ? (
            <div className="border-border/50 bg-muted/20 flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed p-6 text-center">
              <LayoutTemplateIcon className="text-muted-foreground size-8" />
              <h4 className="text-base font-semibold">No templates found</h4>
              <p className="text-muted-foreground max-w-md text-sm">
                Try a different search or filter.
              </p>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 auto-rows-max [grid-template-columns:repeat(auto-fill,minmax(min(100%,300px),1fr))] content-start gap-3 overflow-y-auto p-1">
              {filteredTemplates.map(template => {
                const totalWidgets = getTemplateWidgetBreakdownFromSnapshot(
                  template.elements || [],
                ).reduce((total, widget) => total + widget.count, 0);
                const generatedThumbnail =
                  generateTemplateThumbnailFromSnapshot({
                    elements: template.elements || [],
                    title: template.name,
                    subtitle: `${totalWidgets} widget${totalWidgets === 1 ? "" : "s"}`,
                  });
                const fileBackedThumbnailSrc = (template.thumbnailFileIds || [])
                  .map(fileId => templateThumbnailSignedUrlByFileId[fileId])
                  .find((url): url is string => !!url);
                const thumbnailSrc =
                  fileBackedThumbnailSrc || generatedThumbnail;
                const demoVideoThumbnailSrc = getYouTubeThumbnailUrl(
                  template.demoVideoUrl,
                );
                const cardMediaSrc = demoVideoThumbnailSrc || thumbnailSrc;
                const descriptionPreview =
                  stripTemplateDescriptionMarkdown(
                    template.description || "",
                  ) || "No description provided for this template.";
                const templateIsEditable = canManageTemplate(template);

                return (
                  <div
                    key={template.id}
                    className={cn(
                      "group border-border/60 bg-card/85 hover:border-primary/45 hover:shadow-primary/10 relative overflow-hidden rounded-2xl border p-3.5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg",
                      "after:from-primary/10 after:pointer-events-none after:absolute after:inset-0 after:rounded-2xl after:bg-gradient-to-br after:via-transparent after:to-transparent after:opacity-0 after:transition-opacity after:duration-200 hover:after:opacity-100",
                    )}
                  >
                    <div className="relative z-10 flex flex-col gap-1">
                      <div
                        onClick={() => handleOpenTemplateDetails(template.id)}
                        className="border-border/60 bg-background/40 relative overflow-hidden rounded-xl border hover:cursor-pointer"
                      >
                        <img
                          src={cardMediaSrc}
                          alt={`${template.name} template preview`}
                          className="h-36 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/45 to-transparent" />
                        {template.demoVideoUrl ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white/95 backdrop-blur-sm">
                              <PlayCircleIcon className="size-3.5" />
                              Watch demo
                            </span>
                          </div>
                        ) : null}
                        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                          <Badge
                            variant="secondary"
                            className="rounded-full border border-white/25 bg-black/45 px-2 py-0 text-[10px] text-white backdrop-blur-sm"
                          >
                            {visibilityLabels[template.visibility]}
                          </Badge>
                          {template.visibility === "public" ? (
                            <Badge
                              variant={
                                template.publicationStatus === "approved"
                                  ? "secondary"
                                  : "outline"
                              }
                              className={cn(
                                "rounded-full px-2 py-0 text-[10px]",
                                template.publicationStatus === "approved"
                                  ? "border border-white/25 bg-black/45 text-white backdrop-blur-sm"
                                  : "border border-white/20 bg-black/35 text-white/90 backdrop-blur-sm",
                              )}
                            >
                              {getPublicationStatusLabel(template)}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-2 pt-3">
                        <h4 className="truncate text-[1.06rem] leading-tight font-semibold tracking-[-0.01em]">
                          {template.name}
                        </h4>
                        <p className="text-muted-foreground line-clamp-2 min-h-[2.35rem] text-[13px] leading-[1.35]">
                          {descriptionPreview}
                        </p>
                      </div>
                      {template.team ? (
                        <div className="text-muted-foreground flex items-center justify-end text-[11px]">
                          <Badge
                            variant="secondary"
                            className="rounded-full px-2 py-0 text-[10px]"
                          >
                            <UsersIcon className="size-3" />
                            {template.team.name}
                          </Badge>
                        </div>
                      ) : null}

                      {template.valuePills?.length ? (
                        <div className="flex min-h-6 flex-wrap items-center gap-1.5">
                          {template.valuePills.slice(0, 4).map(pill => (
                            <Badge
                              key={pill}
                              variant="secondary"
                              className="border-primary/30 bg-primary/15 text-primary rounded-full border px-2.5 py-0.5 text-[11px] font-medium"
                            >
                              {pill}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted-foreground flex min-h-6 flex-wrap items-center gap-1.5 text-[11px]">
                          <span className="text-muted-foreground/90 font-medium">
                            Category:
                          </span>
                          {template.category ? (
                            <Badge
                              variant="outline"
                              className="rounded-full px-2 py-0 text-[10px]"
                            >
                              {template.category.name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">
                              Uncategorized
                            </span>
                          )}
                          {template.subcategory ? (
                            <Badge
                              variant="secondary"
                              className="rounded-full px-2 py-0 text-[10px]"
                            >
                              {template.subcategory.name}
                            </Badge>
                          ) : null}
                        </div>
                      )}

                      <div className="border-border/50 mt-2 space-y-2 border-t pt-2.5">
                        {template.canApprove ? (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              className="bg-secondary/60 hover:bg-secondary h-7 rounded-md px-2.5 text-[11px]"
                              onClick={() => handleApproveTemplate(template)}
                              disabled={
                                !!approvingTemplateId &&
                                approvingTemplateId !== template.id
                              }
                            >
                              {approvingTemplateId === template.id ? (
                                <>
                                  <Loader2Icon className="size-3.5 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <CheckIcon className="size-3.5" />
                                  Approve
                                </>
                              )}
                            </Button>
                          </div>
                        ) : null}

                        <div className="flex items-center gap-1.5">
                          {templateIsEditable ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              className="bg-secondary/55 hover:bg-secondary h-8 rounded-lg px-3 text-xs"
                              onClick={() => handleOpenTemplateEditor(template)}
                            >
                              <PencilIcon className="size-3.5" />
                              Edit
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            className="ml-auto h-8 rounded-lg px-3 text-xs font-medium"
                            onClick={() =>
                              handleUseTemplate(template.id, template.name)
                            }
                            disabled={
                              !!usingTemplateId &&
                              usingTemplateId !== template.id
                            }
                          >
                            {usingTemplateId === template.id ? (
                              <>
                                <Loader2Icon className="size-3.5 animate-spin" />
                                Creating...
                              </>
                            ) : (
                              "Use Template"
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Dialog
          open={!!detailsTemplateId}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              setDetailsTemplateId(null);
            }
          }}
        >
          <DialogContent disableClose className="max-w-[980px]">
            <DialogHeader className="flex flex-row items-start justify-between space-y-0 text-left">
              <div className="flex flex-col gap-1">
                <DialogTitle className="flex items-center gap-2">
                  <LayoutTemplateIcon className="size-4" />
                  <span>{detailsTemplate?.name || "Template details"}</span>
                </DialogTitle>
                <DialogDescription>
                  Explore detailed information about this template and its
                  creator.
                </DialogDescription>
              </div>
              <DialogClose asChild>
                <Button variant="secondary" className="rounded-xl" size="sm">
                  <ArrowLeftIcon className="size-4" />
                  Back
                </Button>
              </DialogClose>
            </DialogHeader>

            {isLoadingDetailsTemplate ? (
              <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
                <Skeleton className="h-[340px] rounded-2xl" />
                <Skeleton className="h-[340px] rounded-2xl" />
              </div>
            ) : isDetailsTemplateError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not load template details</AlertTitle>
                <AlertDescription>
                  {detailsTemplateError instanceof Error
                    ? detailsTemplateError.message
                    : "An error occurred while loading template details."}
                </AlertDescription>
              </Alert>
            ) : !detailsTemplate ? (
              <Alert>
                <AlertTitle>Template not found</AlertTitle>
                <AlertDescription>
                  This template may have been removed or is no longer
                  accessible.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr]">
                <div className="border-border/60 bg-card/60 rounded-2xl border p-4">
                  <div className="border-border/60 bg-background/40 mb-4 overflow-hidden rounded-xl border">
                    {detailsActiveSlide?.kind === "video" ? (
                      <iframe
                        title={`${detailsTemplate.name} demo video`}
                        src={detailsActiveSlide.src}
                        className="h-[280px] w-full max-lg:aspect-video max-lg:h-auto"
                        loading="lazy"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    ) : (
                      <img
                        src={
                          detailsActiveSlide?.src || detailsGeneratedThumbnail
                        }
                        alt={`${detailsTemplate.name} preview`}
                        className="h-[280px] w-full object-cover max-lg:aspect-video max-lg:h-auto"
                      />
                    )}
                  </div>
                  {detailsMediaSlides.length > 1 ? (
                    <div className="mb-4 flex items-center justify-between gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-8 rounded-lg px-2"
                        onClick={() =>
                          setDetailsMediaSlideIndex(prev =>
                            Math.max(prev - 1, 0),
                          )
                        }
                        disabled={detailsMediaSlideIndex === 0}
                      >
                        <ChevronLeftIcon className="size-4" />
                        <span className="sr-only">Previous slide</span>
                      </Button>
                      <div className="flex items-center gap-1">
                        {detailsMediaSlides.map((slide, index) => (
                          <button
                            key={`${slide.kind}-${index}`}
                            type="button"
                            onClick={() => setDetailsMediaSlideIndex(index)}
                            className={cn(
                              "size-2 rounded-full transition-colors",
                              detailsMediaSlideIndex === index
                                ? "bg-primary"
                                : "bg-muted-foreground/40 hover:bg-muted-foreground/70",
                            )}
                          >
                            <span className="sr-only">{`Slide ${index + 1}`}</span>
                          </button>
                        ))}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-8 rounded-lg px-2"
                        onClick={() =>
                          setDetailsMediaSlideIndex(prev =>
                            Math.min(prev + 1, detailsMediaSlides.length - 1),
                          )
                        }
                        disabled={
                          detailsMediaSlideIndex >=
                          detailsMediaSlides.length - 1
                        }
                      >
                        <ChevronRightIcon className="size-4" />
                        <span className="sr-only">Next slide</span>
                      </Button>
                    </div>
                  ) : null}

                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    {(() => {
                      const VisibilityIcon =
                        visibilityIcons[detailsTemplate.visibility];

                      return (
                        <Badge variant="outline" className="rounded-full">
                          <VisibilityIcon className="mr-1 size-3.5" />
                          {visibilityLabels[detailsTemplate.visibility]}
                        </Badge>
                      );
                    })()}
                    {detailsTemplate.visibility === "public" ? (
                      <Badge
                        variant={
                          detailsTemplate.publicationStatus === "approved"
                            ? "secondary"
                            : "outline"
                        }
                        className="rounded-full"
                      >
                        {getPublicationStatusLabel(detailsTemplate)}
                      </Badge>
                    ) : null}
                    {detailsTemplate.team ? (
                      <Badge variant="secondary" className="rounded-full">
                        Team: {detailsTemplate.team.name}
                      </Badge>
                    ) : null}
                    {detailsTemplate.category ? (
                      <Badge variant="outline" className="rounded-full">
                        {detailsTemplate.category.name}
                      </Badge>
                    ) : null}
                    {detailsTemplate.subcategory ? (
                      <Badge variant="secondary" className="rounded-full">
                        {detailsTemplate.subcategory.name}
                      </Badge>
                    ) : null}
                  </div>

                  <h4 className="text-foreground text-xl font-semibold">
                    {detailsTemplate.name}
                  </h4>
                  <div className="prose prose-sm dark:prose-invert prose-headings:mb-2 prose-headings:mt-3 prose-p:my-2 prose-p:leading-relaxed prose-blockquote:border-l-2 prose-blockquote:border-border prose-blockquote:pl-3 prose-blockquote:italic prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-pre:border prose-pre:border-border prose-pre:bg-muted/30 prose-ol:my-2 prose-ul:my-2 prose-li:my-0 mt-2 max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, ...props }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {detailsDescriptionMarkdown}
                    </ReactMarkdown>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {detailsTemplate.tags.length ? (
                      detailsTemplate.tags.map(tag => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="rounded-full"
                        >
                          #{tag}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground text-xs">
                        No tags added.
                      </span>
                    )}
                  </div>
                </div>

                <div className="border-border/60 bg-card/60 flex flex-col gap-4 rounded-2xl border p-4">
                  <div>
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Creator
                    </p>
                    <p className="text-foreground mt-1 text-base font-semibold">
                      {detailsTemplate.owner?.displayName || "Unknown creator"}
                    </p>
                  </div>

                  <div>
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Activity
                    </p>
                    <p className="text-foreground mt-1 text-sm">
                      Updated {formatRelativeDate(detailsTemplate.updatedAt)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Version {detailsTemplate.version}
                    </p>
                  </div>

                  <div>
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Widgets
                    </p>
                    <p className="text-foreground mt-1 text-sm">
                      {detailsTotalWidgets} total widget
                      {detailsTotalWidgets === 1 ? "" : "s"} across{" "}
                      {detailsWidgetsWithDescription.length} type
                      {detailsWidgetsWithDescription.length === 1 ? "" : "s"}
                    </p>
                    <div className="mt-2 space-y-2 lg:h-80 lg:overflow-y-auto lg:pr-1">
                      {detailsWidgetsWithDescription.length ? (
                        detailsWidgetsWithDescription.map(widget => (
                          <div
                            key={widget.name}
                            className="border-border/60 bg-background/35 flex items-start gap-2 rounded-xl border p-2.5"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-foreground text-sm leading-tight font-semibold">
                                {widget.label}
                              </p>
                              <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                                {widget.description}
                              </p>
                            </div>
                            <Badge
                              variant="secondary"
                              className="shrink-0 rounded-full px-2 py-0 text-[10px]"
                            >
                              x{widget.count}
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          Widget metadata unavailable.
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto flex flex-col gap-2 sm:flex-row">
                    {detailsTemplate.canApprove ? (
                      <Button
                        variant="outline"
                        className="flex-1 rounded-xl"
                        onClick={() => handleApproveTemplate(detailsTemplate)}
                        disabled={
                          !!approvingTemplateId &&
                          approvingTemplateId !== detailsTemplate.id
                        }
                      >
                        {approvingTemplateId === detailsTemplate.id ? (
                          <>
                            <Loader2Icon className="size-4 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <CheckIcon className="size-4" />
                            Approve
                          </>
                        )}
                      </Button>
                    ) : null}
                    {canManageTemplate(detailsTemplate) ? (
                      <Button
                        variant="outline"
                        className="flex-1 rounded-xl"
                        onClick={() =>
                          handleOpenTemplateEditor(detailsTemplate)
                        }
                      >
                        <PencilIcon className="size-4" />
                        Edit
                      </Button>
                    ) : null}
                    <Button
                      className="flex-1 rounded-xl"
                      onClick={() =>
                        handleUseTemplate(
                          detailsTemplate.id,
                          detailsTemplate.name,
                        )
                      }
                      disabled={
                        !!usingTemplateId &&
                        usingTemplateId !== detailsTemplate.id
                      }
                    >
                      {usingTemplateId === detailsTemplate.id ? (
                        <>
                          <Loader2Icon className="size-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        "Use Template"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog
          open={!!editingTemplate}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              resetEditState();
            }
          }}
        >
          <DialogContent disableClose className="max-w-[960px]">
            <DialogHeader className="flex flex-row items-start justify-between space-y-0 text-left">
              <div className="flex flex-col gap-1">
                <DialogTitle className="flex items-center gap-2">
                  <PencilIcon className="size-4" />
                  <span>
                    Edit{" "}
                    {editingTemplate?.name
                      ? `"${editingTemplate.name}"`
                      : "template"}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Update template details and visibility. Element structure
                  cannot be edited.
                </DialogDescription>
              </div>
              <DialogClose asChild>
                <Button variant="secondary" className="rounded-xl" size="sm">
                  <ArrowLeftIcon className="size-4" />
                  Back
                </Button>
              </DialogClose>
            </DialogHeader>

            {!editingTemplate ? (
              <Alert>
                <AlertTitle>Template not found</AlertTitle>
                <AlertDescription>
                  This template is no longer available for editing.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <div className="flex flex-col gap-4">
                  {editError ? (
                    <Alert variant="destructive">
                      <AlertTitle>Could not update template</AlertTitle>
                      <AlertDescription>{editError}</AlertDescription>
                    </Alert>
                  ) : null}

                  <div className="grid gap-2">
                    <Label htmlFor="template-edit-name">Template Name</Label>
                    <Input
                      id="template-edit-name"
                      value={editName}
                      onChange={event => setEditName(event.target.value)}
                      placeholder="Enter a template name..."
                    />
                  </div>

                  <TemplateDescriptionEditor
                    id="template-edit-description"
                    value={editDescription}
                    onChange={setEditDescription}
                    placeholder="Briefly describe when to use this template..."
                  />

                  <div className="grid gap-2">
                    <Label htmlFor="template-edit-tags">
                      Tags (comma separated)
                    </Label>
                    <Input
                      id="template-edit-tags"
                      value={editTagsInput}
                      onChange={event => setEditTagsInput(event.target.value)}
                      placeholder="sales, analytics, kpi"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="template-edit-demo-video">
                      Demo Video URL (YouTube only)
                    </Label>
                    <Input
                      id="template-edit-demo-video"
                      value={editDemoVideoUrl}
                      onChange={event =>
                        setEditDemoVideoUrl(event.target.value)
                      }
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Template Category</Label>
                    {isLoadingTemplateCategories ? (
                      <Skeleton className="h-10 rounded-lg" />
                    ) : isTemplateCategoriesError ? (
                      <Alert variant="destructive">
                        <AlertTitle>Could not load categories</AlertTitle>
                        <AlertDescription>
                          {templateCategoriesError instanceof Error
                            ? templateCategoriesError.message
                            : "An error occurred while loading categories."}
                        </AlertDescription>
                      </Alert>
                    ) : !templateCategories?.length ? (
                      <Alert>
                        <AlertTitle>No categories available</AlertTitle>
                        <AlertDescription>
                          Categories are not configured yet.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Select
                          value={editCategoryId || ""}
                          onValueChange={value => setEditCategoryId(value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {templateCategories.map(category => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={editSubcategoryId || ""}
                          onValueChange={value => setEditSubcategoryId(value)}
                          disabled={!selectedEditCategory?.subcategories.length}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select subcategory" />
                          </SelectTrigger>
                          <SelectContent>
                            {(selectedEditCategory?.subcategories || []).map(
                              subcategory => (
                                <SelectItem
                                  key={subcategory.id}
                                  value={subcategory.id}
                                >
                                  {subcategory.name}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label>Template Visibility</Label>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {editableVisibilityModes.map(mode => {
                        const Icon = mode.icon;
                        const selected = editVisibility === mode.value;

                        return (
                          <button
                            key={mode.value}
                            type="button"
                            className={cn(
                              "border-border/60 bg-card/40 hover:border-primary/40 hover:bg-card rounded-xl border p-3 text-left transition-colors",
                              {
                                "border-primary/60 bg-primary/10": selected,
                              },
                            )}
                            onClick={() => {
                              setEditVisibility(mode.value);
                              if (mode.value !== "team") {
                                setEditTeamId(null);
                              }
                            }}
                          >
                            <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
                              <Icon className="size-4" />
                              {mode.title}
                            </div>
                            <p className="text-muted-foreground text-xs">
                              {mode.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {editVisibility === "team" ? (
                    <div className="grid gap-2">
                      <Label>Select Team</Label>
                      {isLoadingEditableTeams ? (
                        <div className="grid gap-2">
                          <Skeleton className="h-12 rounded-xl" />
                          <Skeleton className="h-12 rounded-xl" />
                        </div>
                      ) : isEditableTeamsError ? (
                        <Alert variant="destructive">
                          <AlertTitle>Could not load teams</AlertTitle>
                          <AlertDescription>
                            {editableTeamsError instanceof Error
                              ? editableTeamsError.message
                              : "An error occurred while loading teams."}
                          </AlertDescription>
                        </Alert>
                      ) : !editableTeams?.length ? (
                        <Alert>
                          <AlertTitle>No teams found</AlertTitle>
                          <AlertDescription>
                            You need to be in at least one team to publish a
                            team-only template.
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <div className="grid gap-2">
                          {editableTeams.map(team => (
                            <button
                              key={team.id}
                              type="button"
                              className={cn(
                                "border-border/60 bg-card/40 hover:border-primary/40 hover:bg-card flex items-center justify-between rounded-xl border px-3 py-2 text-left transition-colors",
                                {
                                  "border-primary/60 bg-primary/10":
                                    editTeamId === team.id,
                                },
                              )}
                              onClick={() => setEditTeamId(team.id)}
                            >
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold">
                                  {team.name}
                                </span>
                                <span className="text-muted-foreground text-xs">
                                  {team.owner?.displayName
                                    ? `Owner: ${team.owner.displayName}`
                                    : "Team workspace"}
                                </span>
                              </div>
                              {editTeamId === team.id ? (
                                <Badge
                                  variant="default"
                                  className="rounded-full"
                                >
                                  Selected
                                </Badge>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveTemplateDetails}
                      disabled={
                        isUpdatingTemplate ||
                        !editCategoryId ||
                        !editSubcategoryId ||
                        (editVisibility === "team" && !editableTeams?.length)
                      }
                    >
                      {isUpdatingTemplate ? (
                        <>
                          <Loader2Icon className="size-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <PencilIcon className="size-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="border-border/60 bg-muted/20 rounded-xl border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-sm font-bold">Current Visibility</h4>
                    <Badge variant="outline">
                      {visibilityLabels[editingTemplate.visibility]}
                    </Badge>
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-1">
                    {editingTemplate.category ? (
                      <Badge variant="outline" className="rounded-full">
                        {editingTemplate.category.name}
                      </Badge>
                    ) : null}
                    {editingTemplate.subcategory ? (
                      <Badge variant="secondary" className="rounded-full">
                        {editingTemplate.subcategory.name}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-muted-foreground mb-3 text-xs">
                    Updating details for public templates will move them back to
                    pending approval.
                  </p>
                  <div className="border-border/50 bg-background/40 rounded-lg border p-3">
                    <p className="text-muted-foreground text-xs tracking-wide uppercase">
                      Last updated
                    </p>
                    <p className="text-foreground mt-1 text-sm font-medium">
                      {formatRelativeDate(editingTemplate.updatedAt)}
                    </p>
                    <p className="text-muted-foreground mt-3 text-xs tracking-wide uppercase">
                      Creator
                    </p>
                    <p className="text-foreground mt-1 text-sm font-medium">
                      {editingTemplate.owner?.displayName || "Unknown creator"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
